import { Box, Typography } from '@mui/material';
import { Field, FieldProps } from 'formik';

interface PrivacyConsentSectionProps {
  /** Formik field name for the checkbox (default: 'consent') */
  fieldName?: string;
  /** HTML id for the checkbox element (default: 'consent-checkbox') */
  checkboxId?: string;
  /** Bold label text next to the checkbox */
  label: string;
  /** Disables the checkbox when true */
  isLoading: boolean;
}

/**
 * Reusable privacy consent section with:
 * - "Datenschutz:" heading
 * - LANSCO privacy policy link and explanation text
 * - Checkbox with configurable label and Formik error handling
 */
export const PrivacyConsentSection = ({
  fieldName = 'consent',
  checkboxId = 'consent-checkbox',
  label,
  isLoading,
}: PrivacyConsentSectionProps) => (
  <Box sx={{ mt: 3 }}>
    <Typography variant="subtitle1" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
      Datenschutz:
    </Typography>
    <Typography variant="body2" component="div" sx={{ mb: 2 }}>
      Ich bin damit einverstanden, dass meine personenbezogenen Daten gemäß den{' '}
      <a href="https://www.lansco.de/lansco/datenschutz.html" target="_blank" rel="noopener noreferrer">
        Datenschutzbestimmungen
      </a>{' '}
      der LANSCO GmbH verarbeitet werden. Bei einem Widerspruch fallen keine weiteren Kosten, außer die üblichen
      Übermittlungskosten, an.
    </Typography>
    <Field type="checkbox" name={fieldName}>
      {({ field, form }: FieldProps<boolean>) => (
        <div>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <input
              type="checkbox"
              onChange={field.onChange}
              onBlur={field.onBlur}
              name={field.name}
              id={checkboxId}
              checked={field.value}
              style={{ marginRight: '10px', position: 'relative', top: '2px' }}
              disabled={isLoading}
            />
            <label htmlFor={checkboxId}>
              <Typography variant="body2" component="span">
                <strong>{label}</strong>
              </Typography>
            </label>
          </Box>
          {form.touched[fieldName] && form.errors[fieldName] && (
            <Typography color="error" variant="caption" sx={{ mt: 1, display: "block" }}>
              {form.errors[fieldName] as string}
            </Typography>
          )}
        </div>
      )}
    </Field>
  </Box>
);
